import React, { useState, useEffect } from "react";
import "./MobileCategoryList.css";
import {
  FaTrash,
  FaBuilding,
  FaUser,
  FaCalendarAlt,
  FaToggleOn,
  FaPlus,
} from "react-icons/fa";
import img from "../MobileCategory/MobileCategoryHeader/logo.png";
import axios from "axios";
import { Api } from "../../BackendApi/api";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Component/Spinner/Spinner";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatIndianDate } from "../../Component/IndFormatDate/formatIndianDate";
import MobileConfirmPopup from "../../Component/MobileConfirmPopup/MobileConfirmPopup";
import MobileDeletePopup from "../../Component/MobileDeletePopup/MobileDeletePopup";
import MobileEditCategoryOverlay from "./MobileEditCategoryOverlay/MobileEditCategoryOverlay";
import MobileCategoryOverlay from "./MobileCategoryOverlay";
import MobileCategoryHeader from "./MobileCategoryHeader/MobileCategoryHeader";

const columnToApiMap = {
  categoryName: "CategoryName",
  description: "Description",
  status: "Status",
  companyName: "CompName",
  createdBy: "CreatedByAdminName",
  lastUpdated: "LastUpdate",
  createdAt: "LastUpdate",
};

const MobileCategoryList = () => {
  const [showLogout, setShowLogout] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnSearch, setColumnSearch] = useState({
    categoryName: "",
    description: "",
    companyName: "",
    createdBy: "",
    status: "Active",
    lastUpdated: "",
  });
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [popup, setPopup] = useState({ show: false, message: "", type: "success" });

  const navigate = useNavigate();
  const limit = 20;

  const [sortState, setSortState] = useState({
    column: "lastUpdated",
    order: "desc",
  });

  const fetchCategories = async (pageNum = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/");
        return;
      }

      const startfrom = (pageNum - 1) * limit;
      const apiSortColumn = columnToApiMap[sortState.column] || "LastUpdate";
      const apiSortOrder = sortState.order === "asc" ? 1 : -1;

      const payload = {
        startfrom,
        limit,
        categoryName: columnSearch.categoryName || "",
        description: columnSearch.description || "",
        companyName: columnSearch.companyName || "",
        createdBy: columnSearch.createdBy || "",
        status: columnSearch.status || "",
        searchstring: globalSearch || "",
        SortColumn: apiSortColumn,
        SortOrder: apiSortOrder,
      };

      const res = await axios.post(Api.getCategories, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.status === 1 && Array.isArray(res.data.data)) {
        const categories = res.data.data.map((item) => ({
          _id: item._id,
          categoryName: item.CategoryName || "",
          description: item.Description || "",
          image: item.CategoryImagePath
            ? `${Api.baseurl.replace(/\/$/, "")}/${item.CategoryImagePath.replace(/^\//, "")}`
            : img,
          status: item.Status || "Deactive",
          companyName: item.CompName || "N/A",
          createdBy: item.CreatedByAdminName || "Admin",
          lastUpdated: item.LastUpdate ? formatIndianDate(item.LastUpdate) : "-",
        }));

        setData(pageNum === 1 ? categories : [...data, ...categories]);
        if (categories.length < limit) setHasMore(false);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    const handler = setTimeout(() => fetchCategories(1), 300);
    return () => clearTimeout(handler);
  }, [globalSearch, columnSearch, sortState]);

  const fetchMoreData = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchCategories(nextPage);
  };

  // 🟣 Open Add Category Overlay
  const handleAddCategory = () => {
    setShowAddModal(true);
    setShowEditModal(false);
  };

  // 🟢 Open Edit Overlay on Card Click
  const handleCardClick = (row) => {
    setEditCategory(row);
    setShowEditModal(true);
    setShowAddModal(false);
  };

  const handleDeleteClick = (e, id) => {
    e.stopPropagation(); // Prevent triggering card click
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/");
        return;
      }

      const res = await axios.post(
        Api.editCategory,
        { _id: selectedId, Status: "Deactive" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === 1) {
        setPopup({
          show: true,
          message: "Category deactivated successfully!",
          type: "success",
        });
        setData([]);
        fetchCategories(1);
      }
    } catch (err) {
      console.error("Error deleting:", err);
    } finally {
      setShowDeleteModal(false);
      setSelectedId(null);
    }
  };

  const handlePopupClose = () => setPopup({ ...popup, show: false });

  return (
    <div className={`mobile-category-wrapper ${showLogout ? "blur-screen" : ""}`}>
    <div className="mobile-category-table-container">
      <MobileCategoryHeader
        search={globalSearch}
        setSearch={setGlobalSearch}
        columnSearch={columnSearch}
        setColumnSearch={setColumnSearch}
        onRefresh={() => {
          setData([]);
          setPage(1);
          fetchCategories(1);
        }}
      />

      <div className="mobile-category-grid-table" id="scrollableDiv">
        {loading && page === 1 ? (
          <Spinner />
        ) : data.length > 0 ? (
          <InfiniteScroll
            dataLength={data.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={<Spinner />}
            endMessage={<div className="mobile-no-data">No more categories</div>}
            scrollableTarget="scrollableDiv"
            style={{ overflow: "visible" }}
          >
            {data.map((row) => (
              <div
                className="mobile-category-card"
                key={row._id}
                onClick={() => handleCardClick(row)} // ✅ Full card click opens edit overlay
              >
                <div className="mobile-category-top">
                  <div className="mobile-category-left">
                    <img
                      src={row.image}
                      alt={row.categoryName}
                      className="mobile-category-img"
                      onError={(e) => (e.target.src = img)}
                    />
                    <div className="mobile-category-details">
                      <h4 className="mobile-category-title">{row.categoryName}</h4>
                      <span className="mobile-category-company">
                        <FaBuilding /> {row.companyName}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mobile-category-info">
                  <span><FaToggleOn /> {row.status}</span>
                  <span><FaUser /> {row.createdBy}</span>
                </div>

                <div className="mobile-category-bottom">
                  <div className="mobile-category-date">
                    <FaCalendarAlt /> {row.lastUpdated}
                  </div>
                  <div className="mobile-category-action-icons">
                    <button
                      className="mobile-delete"
                      onClick={(e) => handleDeleteClick(e, row._id)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </InfiniteScroll>
        ) : (
          <div className="mobile-no-data">{loading ? "Loading..." : "No categories found"}</div>
        )}
      </div>

      {/* ✅ Floating Add Button */}
      {!showEditModal && !showAddModal && (
        <button className="floating-add-btn" onClick={handleAddCategory}>
          <FaPlus />
        </button>
      )}

      {/* ✅ Add Overlay */}
      {showAddModal && (
        <MobileCategoryOverlay
          onClose={() => setShowAddModal(false)}
          onSave={() => {
            setShowAddModal(false);
            setData([]);
            fetchCategories(1);
          }}
        />
      )}

      {/* ✅ Edit Overlay */}
      {showEditModal && (
        <MobileEditCategoryOverlay
          category={editCategory}
          onClose={() => setShowEditModal(false)}
          onSave={() => {
            setShowEditModal(false);
            setData([]);
            fetchCategories(1);
          }}
        />
      )}

      <MobileDeletePopup
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
      />

      <MobileConfirmPopup
        show={popup.show}
        message={popup.message}
        type={popup.type}
        onClose={handlePopupClose}
      />
    </div>
    </div>
  );
};

export default MobileCategoryList;
